import { useCallback, useEffect, useState } from "react";
import { api, type CallFlow, type CallFlowSchedule } from "./api.ts";
import { FlowSchedule } from "./CallFlowSchedule.tsx";
import { flowIsAnswerable } from "./flow/layout.ts";

// Dedicated "Programmation" tab: the auto/override status banner and
// the weekly schedule rules, moved out of the flow editor so the
// editor screen is purely about designing graphs.

export function SchedulePage(props: { token: string }) {
  const [flows, setFlows] = useState<CallFlow[] | null>(null);
  const [schedules, setSchedules] = useState<CallFlowSchedule[] | null>(null);
  const [overrideFlowId, setOverrideFlowId] = useState<string | null>(null);
  const [cooldownMin, setCooldownMin] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [flowsRes, overrideRes, cooldownRes] = await Promise.all([
        api.listFlows(props.token),
        api.getActiveOverride(props.token),
        api.getRingCooldown(props.token),
      ]);
      setFlows(flowsRes.flows);
      setSchedules(flowsRes.schedules);
      setOverrideFlowId(overrideRes.flowId);
      setCooldownMin(Math.round((cooldownRes.seconds / 60) * 10) / 10);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [props.token]);

  async function saveCooldown(minutes: number): Promise<void> {
    const seconds = Math.max(0, Math.min(3600, Math.round(minutes * 60)));
    setBusy(true);
    try {
      await api.setRingCooldown(props.token, seconds);
      await refresh();
    } catch (err) {
      setError(`cooldown: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function clearOverride(): Promise<void> {
    setBusy(true);
    try {
      await api.setActiveOverride(props.token, null);
      await refresh();
    } catch (err) {
      setError(`override: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /** Force a flow immediately, bypassing the schedule until cleared. */
  async function forceActive(id: string): Promise<void> {
    const name = flows?.find((f) => f.id === id)?.name ?? id;
    if (!confirm(`Activer « ${name} » immédiatement ? La programmation horaire sera ignorée jusqu'à désactivation.`)) return;
    setBusy(true);
    try {
      await api.setActiveOverride(props.token, id);
      await refresh();
    } catch (err) {
      setError(`override: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const defaultFlow = flows?.find((f) => f.isDefault);

  return (
    <>
      {error && <div className="error">{error}</div>}

      {overrideFlowId ? (
        <div className="flow-override-banner">
          <span>
            ⚡ <strong>Activation manuelle</strong> :{" "}
            <em>{flows?.find((f) => f.id === overrideFlowId)?.name ?? "(flux supprimé)"}</em>{" "}
            — la programmation horaire ci-dessous est ignorée jusqu'à désactivation.
          </span>
          <span style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
            <select
              value=""
              onChange={(e) => e.target.value && void forceActive(e.target.value)}
              disabled={busy}
              title="Basculer l'activation manuelle sur un autre flux"
            >
              <option value="">⚡ Changer de flux…</option>
              {flows?.filter((f) => f.id !== overrideFlowId).map((f) => {
                const mute = !flowIsAnswerable(f.graph);
                return (
                  <option key={f.id} value={f.id} disabled={mute}>
                    {f.name}{mute ? " — muet (ne décroche pas)" : ""}
                  </option>
                );
              })}
            </select>
            <button className="ghost" type="button" onClick={() => void clearOverride()} disabled={busy}>
              Reprendre la programmation
            </button>
          </span>
        </div>
      ) : flows && flows.length > 0 ? (
        <div className="flow-status-banner">
          <span>
            🗓 <strong>Programmation auto</strong> — l'appel suit les règles horaires
            ci-dessous{" "}
            {defaultFlow ? (
              <>
                ; sans règle correspondante, le repli est <em>{defaultFlow.name}</em>{" "}
                <span className="muted">(flux par défaut)</span>.
              </>
            ) : (
              <>
                . <span className="muted">Aucun flux n'est marqué « par défaut » —
                dans l'éditeur de flux, cliquez « ⭐ Définir comme repli » sur l'un
                d'eux pour qu'il serve de repli quand aucune règle ne s'applique.</span>
              </>
            )}
          </span>
        </div>
      ) : null}

      {cooldownMin != null && (
        <div className="card" style={{ marginTop: "0.75rem" }}>
          <div className="card-header"><h3>Temps de récupération après appel</h3></div>
          <p className="muted" style={{ padding: "0 0.25rem", margin: "0 0 0.5rem" }}>
            Un agent qui vient de terminer un appel n'est pas resonné pendant ce
            délai (il reste joignable si c'est le seul disponible). 0 = désactivé.
          </p>
          <div className="row" style={{ gap: "0.5rem", alignItems: "center", padding: "0 0.25rem" }}>
            <input
              type="number"
              min={0}
              max={60}
              step={0.5}
              value={cooldownMin}
              onChange={(e) => setCooldownMin(parseFloat(e.target.value))}
              disabled={busy}
              style={{ width: "5rem" }}
            />
            <span>minute(s)</span>
            <button
              className="primary"
              type="button"
              disabled={busy || cooldownMin == null}
              onClick={() => void saveCooldown(cooldownMin ?? 0)}
            >
              Enregistrer
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              Prise en compte sous ~20 s.
            </span>
          </div>
        </div>
      )}

      {flows && schedules ? (
        <FlowSchedule
          token={props.token}
          flows={flows}
          schedules={schedules}
          onChange={() => void refresh()}
        />
      ) : (
        !error && <div className="empty">Chargement…</div>
      )}
    </>
  );
}
